import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_TransparentBackground = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/transparent-background',
			name: 'Transparent Background Maker — Make PNG Transparent | Lift',
			description:
				'Create transparent backgrounds instantly with AI. No watermark. Make image transparent, convert JPG to PNG & edit images online. Try now!',
			url: 'https://liftapp.ai/transparent-background',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/transparent-background#product' },
				{ '@id': 'https://liftapp.ai/transparent-background#howto' },
				{ '@id': 'https://liftapp.ai/transparent-background#benefits' },
				{ '@id': 'https://liftapp.ai/transparent-background#faq' },
				{ '@id': 'https://liftapp.ai/transparent-background#why-lift' },
				{ '@id': 'https://liftapp.ai/transparent-background#explore-tools' },
				{ '@id': 'https://liftapp.ai/transparent-background#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/transparent-background#product',
			name: 'Transparent Background Maker Online',
			description:
				'Make any image transparent in seconds with AI precision. Create transparent PNG backgrounds, convert JPG to PNG, and edit images online, no watermarks.',
			url: 'https://liftapp.ai/transparent-background',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/transparent-background#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift automatically makes the background transparent.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/transparent-background#benefits',
			name: 'Who can benefit from Transparent Background Maker?',
			description:
				"Lift's AI Transparent Background Maker is useful for professionals, creators, and businesses who want clean, transparent PNG images for e-commerce, social media, marketing, or personal use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'E-commerce Sellers — Create professional product images with transparent backgrounds for Amazon, Shopify, or Etsy listings. Make PNG transparent in bulk to prepare clean catalog photos that boost conversions and build customer trust.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content Creators — Design eye-catching Instagram posts, YouTube thumbnails, and TikTok content with transparent PNGs. Make image backgrounds transparent to create unique collages, memes, and covers without expensive graphic software.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers & Freelancers — Speed up your workflow with instant transparent background generation and PNG conversion. Deliver professional transparent images to clients faster, eliminate manual retouching, and take on more projects to increase earnings.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small Business Owners — Create marketing materials, logos, flyers, and presentations with transparent PNG images. Make background transparent without hiring a designer — produce professional visuals for ads, social media, and print in minutes.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Generate transparent PNGs for school projects, presentations, and creative assignments. Make picture transparent to design professional posters, reports, and digital portfolios that stand out.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'JPG to PNG transparent',
				description:
					'Example of how Lift Transparent Background Maker creates transparent PNG images for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/transparent-background#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Transparent Background Maker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Transparent Background Maker is a premium online tool that instantly converts images to transparent PNG with pixel-perfect precision — ideal for product photos, logos, profile pictures, or graphic design.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Transparent Background Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's transparent background tool. Our AI automatically detects the subject and makes the background transparent in seconds — no photo editing experience needed. Download up to 20 transparent PNG images.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Transparent Background Maker uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking transparent PNGs without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/transparent-background#why-lift',
			name: "Why Lift's AI Transparent Background Maker stands out",
			description:
				"Lift's Transparent Background Maker combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Image with transparent background',
				description:
					"Comparison of Lift's AI Transparent Background Maker benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/transparent-background#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/transparent-background#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Transparent Background Maker',
					item: 'https://liftapp.ai/transparent-background',
				},
			],
		},
	],
};
